<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import PlugZap from '@lucide/vue/dist/esm/icons/plug-zap.mjs'; import RefreshCw from '@lucide/vue/dist/esm/icons/refresh-cw.mjs'; import LayoutDashboard from '@lucide/vue/dist/esm/icons/layout-dashboard.mjs';
import { useModuleAvailability } from '@/modules';

const props = defineProps<{ appLabel: string }>();

const { t } = useI18n();
const router = useRouter();
const { refresh } = useModuleAvailability();

const checking = ref(false);
async function retry() {
  if (checking.value) return;
  checking.value = true;
  try {
    await refresh();
  } finally {
    checking.value = false;
  }
}
function goHome() {
  router.push('/');
}
</script>

<template>
  <div class="cx-gate">
    <div class="cx-gate-card">
      <div class="cx-gate-icon">
        <PlugZap class="nxt-icon-xl" />
      </div>
      <h1 class="cx-gate-title">{{ t('connect.gate.title') }}</h1>
      <p class="cx-gate-text">{{ t('connect.gate.message', { name: props.appLabel }) }}</p>
      <div class="cx-gate-actions">
        <button class="cx-gate-btn primary" type="button" :disabled="checking" @click="retry">
          <RefreshCw :class="{ spin: checking }" class="nxt-icon-sm" />
          <span>{{ t('connect.gate.retry') }}</span>
        </button>
        <button class="cx-gate-btn" type="button" @click="goHome">
          <LayoutDashboard class="nxt-icon-sm" />
          <span>{{ t('connect.gate.home') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cx-gate {
  display: grid;
  place-items: center;
  height: 100%;
  width: 100%;
  padding: var(--nxt-space-xl);
  background: var(--nxt-surface-sunken-bg);
}
.cx-gate-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--nxt-space-sm);
  max-width: var(--nxt-panel-width-2xl);
  padding: var(--nxt-space-xl) calc(var(--nxt-space-md) * 1.75);
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-xl);
  box-shadow: var(--nxt-shadow-lg);
}
.cx-gate-icon {
  display: grid;
  place-items: center;
  width: var(--nxt-tile-size-xl);
  height: var(--nxt-tile-size-xl);
  border-radius: var(--nxt-radius-xl);
  color: var(--nxt-danger-fg);
  background: var(--nxt-danger-subtle-bg);
}
.cx-gate-title {
  margin: var(--nxt-space-2xs) 0 0;
  font-size: var(--nxt-font-size-lg);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
}
.cx-gate-text {
  margin: 0;
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-muted);
}
.cx-gate-actions {
  display: flex;
  gap: calc(var(--nxt-space-xs) * 1.25);
  margin-top: calc(var(--nxt-space-xs) * 1.25);
}
.cx-gate-btn {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--nxt-space-xs) * 0.875);
  padding: calc(var(--nxt-space-md) * 0.563) calc(var(--nxt-space-md) * 0.875);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  cursor: pointer;
  transition: background var(--nxt-motion-fast), border-color var(--nxt-motion-fast);
}
.cx-gate-btn:hover {
  background: var(--nxt-neutral-subtle-bg);
}
.cx-gate-btn.primary {
  background: var(--nxt-brand-bg);
  border-color: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}
.cx-gate-btn.primary:hover {
  background: var(--nxt-brand-bg-hover);
}
.cx-gate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.spin {
  animation: cx-gate-spin 0.8s linear infinite;
}
@keyframes cx-gate-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
